import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import s from './HeroVariants.module.css';

/* ── Images ── */
const IMG = {
  atelier:  '/hero-images/zohar-25.webp',
  manor:    '/hero-images/gal-living.webp',
  salon:    '/hero-images/sivan-31.webp',
  index:    '/hero-images/zohar-21.webp',
  aperture: '/hero-images/sivan-kitchen.webp',
  triptych: [
    '/hero-images/zohar-19.webp',
    '/hero-images/gila-dining.webp',
    '/hero-images/sivan-8.webp',
  ],
  dispatchDesktop: [
    '/hero-images/zohar-25.webp',
    '/hero-images/zohar-24.webp',
    '/hero-images/zohar-23.webp',
    '/hero-images/sivan-22.webp',
    '/hero-images/zohar-17.webp',
  ],
  dispatchMobile: [
    '/hero-images/zohar-kitchen-m.webp',
    '/hero-images/gal-13-m.webp',
    '/hero-images/gila-dining-m.webp',
    '/hero-images/hila-pool-m.webp',
    '/hero-images/gal-living-m.webp',
  ],
  marquee:  '/hero-images/zohar-23.webp',
};

const DISPATCH_INTERVAL = 6200;
const DISPATCH_TRANSITION = 2.2;

/* ════════════════════════════════════════════════════
   VARIANT I - ATELIER
   Split layout: text panel (right, cream) | image (left)
   Most legible. Old-money estate-brochure aesthetic.
   ════════════════════════════════════════════════════ */
function VariantAtelier({ onOpenLead }) {
  return (
    <div className={s.atelier}>
      {/* Right - cream text panel */}
      <div className={s.atelierPanel}>
        <div className={s.atelierMark}>
          <span className={s.atelierMarkLine} />
          <span className={s.atelierMarkText}>Studio Langer</span>
        </div>

        <div className={s.atelierBody}>
          <span className={s.atelierTag}>אדריכלות ועיצוב פנים · תל אביב</span>

          <h1 className={s.atelierHeadline}>
            <span className={s.atelierHl1}>מחזון</span>
            <span className={s.atelierRule} />
            <span className={s.atelierHl2}>לבית</span>
          </h1>

          <p className={s.atelierSub}>
            סטודיו לאדריכלות ועיצוב פנים המלווה כל פרויקט - מהאפיון
            הראשוני ועד לרגע שבו המפתח עובר יד.
            מורכבות היא ההתמחות. חזון הוא נקודת הפתיחה.
          </p>

          <div className={s.atelierCtas}>
            <button className={s.atelierPrimary} onClick={onOpenLead}>
              לפגישת היכרות
            </button>
            <button className={s.atelierSecondary}>
              צפייה בעבודות
            </button>
          </div>
        </div>

        <div className={s.atelierStats}>
          <div className={s.atelierStat}>
            <span className={s.atelierStatNum}>16+</span>
            <span className={s.atelierStatLabel}>שנות ניסיון</span>
          </div>
          <span className={s.atelierStatDiv} />
          <div className={s.atelierStat}>
            <span className={s.atelierStatNum}>50+</span>
            <span className={s.atelierStatLabel}>פרויקטים</span>
          </div>
          <span className={s.atelierStatDiv} />
          <div className={s.atelierStat}>
            <span className={s.atelierStatNum}>100%</span>
            <span className={s.atelierStatLabel}>ליווי אישי</span>
          </div>
        </div>
      </div>

      {/* Left - image */}
      <div className={s.atelierImageWrap}>
        <img src={IMG.atelier} alt="" className={s.atelierImg} />
        <div className={s.atelierImgOverlay} />
        <span className={s.atelierCornerTL} />
        <span className={s.atelierCornerBR} />
        <div className={s.atelierCaption}>
          <span>Zohar</span>
          <span className={s.atelierCaptionLine} />
          <span>תל אביב</span>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   VARIANT II - MANOR
   Full-bleed image, centered content, maximum refinement.
   Luxury hotel lobby - symmetrical, authoritative, opulent.
   ════════════════════════════════════════════════════ */
function VariantManor({ onOpenLead }) {
  return (
    <div className={s.manor}>
      <img src={IMG.manor} alt="" className={s.manorImg} />
      <div className={s.manorOverlay} />

      {/* Top: studio mark */}
      <div className={s.manorTop}>
        <span className={s.manorTopLine} />
        <span className={s.manorTopText}>LANGER · STUDIO · MMVIII</span>
        <span className={s.manorTopLine} />
      </div>

      {/* Center: main content */}
      <div className={s.manorCenter}>
        <p className={s.manorEyebrow}>אדריכלות ועיצוב פנים</p>

        <h1 className={s.manorHeadline}>
          <span>כשהאדריכלות</span>
          <span className={s.manorOrnament}>
            <span className={s.manorOrnLine} />
            <span className={s.manorOrnGem}>◆</span>
            <span className={s.manorOrnLine} />
          </span>
          <em className={s.manorHeadlineEm}>פוגשת את החלום</em>
        </h1>

        <p className={s.manorSub}>
          פרויקטים מלאים של אדריכלות ועיצוב - מהניסוח הראשוני ועד לפרט האחרון.
          <br />כל חלל מקבל שפה ייחודית, בנויה במדויק לאורח החיים.
        </p>

        <button className={s.manorCta} onClick={onOpenLead}>
          <span className={s.manorCtaText}>לשיחת היכרות</span>
          <span className={s.manorCtaArrow}>←</span>
        </button>
      </div>

      {/* Bottom */}
      <div className={s.manorBottom}>
        <span className={s.manorLocation}>תל אביב · ישראל</span>
        <div className={s.manorPips}>
          <span className={s.manorPipActive} />
          <span className={s.manorPip} />
          <span className={s.manorPip} />
        </div>
        <span className={s.manorScroll}>S C R O L L</span>
      </div>

      {/* Corner marks */}
      <span className={s.mCornerTL} />
      <span className={s.mCornerTR} />
      <span className={s.mCornerBL} />
      <span className={s.mCornerBR} />
    </div>
  );
}

/* ════════════════════════════════════════════════════
   VARIANT III - SALON
   Full-bleed, asymmetric editorial layout.
   Massive typographic headline, left-aligned, vertical year.
   Architecture-magazine aesthetic.
   ════════════════════════════════════════════════════ */
function VariantSalon({ onOpenLead }) {
  return (
    <div className={s.salon}>
      <img src={IMG.salon} alt="" className={s.salonImg} />
      <div className={s.salonOverlay} />

      {/* Background watermark */}
      <span className={s.salonWatermark} aria-hidden="true">LANGER</span>

      {/* Vertical year - right edge */}
      <div className={s.salonYearWrap} aria-hidden="true">
        <span className={s.salonYear}>MMVIII</span>
      </div>

      {/* Main content - right-aligned (RTL start) */}
      <div className={s.salonContent}>
        <div className={s.salonTopRow}>
          <span className={s.salonStudio}>Studio Langer</span>
          <span className={s.salonTopLine} />
          <span className={s.salonTagline}>Architecture & Design</span>
        </div>

        <h1 className={s.salonHeadline}>
          <span className={s.salonHSmall}>מחזון</span>
          <span className={s.salonHBig}>לבית</span>
          <span className={s.salonHSub}>ולחיים שבתוכו</span>
        </h1>

        <p className={s.salonDesc}>
          כל חלל הוא מסע - מרגע הפגישה הראשונה ועד לחלל שמדבר בשפה
          ייחודית ומושלמת. כי בית טוב הוא לא רק יפה - הוא חי.
        </p>

        <div className={s.salonActions}>
          <button className={s.salonBtn} onClick={onOpenLead}>
            לפגישת היכרות
            <span className={s.salonBtnArrow}>←</span>
          </button>
          <div className={s.salonMeta}>
            <span className={s.salonMetaItem}>
              <span className={s.salonMetaNum}>50+</span>
              <span className={s.salonMetaLabel}>פרויקטים</span>
            </span>
            <span className={s.salonMetaSep} />
            <span className={s.salonMetaItem}>
              <span className={s.salonMetaNum}>16+</span>
              <span className={s.salonMetaLabel}>שנים</span>
            </span>
          </div>
        </div>
      </div>

      {/* Bottom horizontal strip */}
      <div className={s.salonBottom}>
        <span className={s.salonBotLine} />
        <span className={s.salonBotText}>Architecture & Interior Design · Tel Aviv · Israel</span>
        <span className={s.salonBotLine} />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   VARIANT IV - INDEX
   Editorial register: oversized numbered service list
   in cream column, tall portrait image inset.
   Inspired by Snøhetta / OMA / Studio MK27.
   ════════════════════════════════════════════════════ */
function VariantIndex({ onOpenLead }) {
  const SERVICES = [
    { num: '01', title: 'אדריכלות',     sub: 'תכנון · היתרים · פיקוח' },
    { num: '02', title: 'עיצוב פנים',   sub: 'חומרים · פרטים · ריהוט' },
    { num: '03', title: 'Total Design', sub: 'תחת קורת גג אחת' },
  ];

  return (
    <div className={s.index}>
      {/* Cream editorial column (visually right in RTL) */}
      <div className={s.indexCol}>
        <header className={s.indexHead}>
          <div className={s.indexMark}>
            <span className={s.indexMarkLetters}>L/A</span>
            <span className={s.indexMarkLine} />
            <span className={s.indexMarkText}>לנגר אדריכלות ועיצוב</span>
          </div>
          <div className={s.indexHeadMeta}>
            <span>Index</span>
            <span className={s.indexHeadDot}>·</span>
            <span>Services</span>
          </div>
        </header>

        <ol className={s.indexList}>
          {SERVICES.map((srv) => (
            <li key={srv.num} className={s.indexItem}>
              <span className={s.indexItemNum}>{srv.num}</span>
              <div className={s.indexItemBody}>
                <h2 className={s.indexItemTitle}>{srv.title}</h2>
                <span className={s.indexItemSub}>{srv.sub}</span>
              </div>
              <span className={s.indexItemRule} />
            </li>
          ))}
        </ol>

        <footer className={s.indexFoot}>
          <button className={s.indexCta} onClick={onOpenLead}>
            <span className={s.indexCtaText}>לפגישת היכרות</span>
            <span className={s.indexCtaArrow}>←</span>
          </button>
          <div className={s.indexSig}>
            <span className={s.indexSigLine} />
            <div>
              <p className={s.indexSigName}>לירית לנגר</p>
              <p className={s.indexSigRole}>אדריכלית · מייסדת הסטודיו</p>
            </div>
          </div>
        </footer>
      </div>

      {/* Image side (visually left in RTL) */}
      <figure className={s.indexImageWrap}>
        <div className={s.indexImageInner}>
          <img src={IMG.index} alt="" className={s.indexImage} />
          <div className={s.indexImageOverlay} />
        </div>
        <span className={s.indexFrame} aria-hidden="true" />
        <span className={s.indexImageStamp} aria-hidden="true">EST · MMVIII</span>
        <figcaption className={s.indexImageCaption}>
          <span className={s.indexImageCapLine} />
          <span>Residential · Alonei Aba</span>
        </figcaption>
      </figure>
    </div>
  );
}


/* ════════════════════════════════════════════════════
   VARIANT V - APERTURE
   Centered framed portrait inside a cream void.
   Heavy negative space, contemplative.
   Inspired by John Pawson / Vincent Van Duysen / Studio KO.
   ════════════════════════════════════════════════════ */
function VariantAperture({ onOpenLead }) {
  return (
    <div className={s.aperture}>
      <span className={s.apertureCenterLine} aria-hidden="true" />

      {/* Top masthead */}
      <div className={s.apertureTop}>
        <span className={s.apertureTopLine} />
        <span className={s.apertureTopText}>LANGER · ARCHITECTURE &amp; DESIGN</span>
        <span className={s.apertureTopLine} />
      </div>

      {/* Vertical rails (decorative) */}
      <div className={s.apertureLeftRail} aria-hidden="true">
        <span>EST · MMVIII</span>
      </div>
      <div className={s.apertureRightRail} aria-hidden="true">
        <span>ALONEI ABA · IL</span>
      </div>

      {/* Centered framed image */}
      <figure className={s.apertureFigure}>
        <span className={s.apertureCornerTL} />
        <span className={s.apertureCornerTR} />
        <span className={s.apertureCornerBL} />
        <span className={s.apertureCornerBR} />
        <div className={s.apertureFrameOuter}>
          <div className={s.apertureFrameInner}>
            <img src={IMG.aperture} alt="" className={s.apertureImg} />
          </div>
        </div>
        <figcaption className={s.apertureCap}>
          <span>Plate I</span>
          <span className={s.apertureCapDot}>·</span>
          <span>Residence</span>
        </figcaption>
      </figure>

      {/* Bottom content */}
      <div className={s.apertureBottom}>
        <span className={s.apertureTag}>Studio Manifesto</span>
        <h1 className={s.apertureHeadline}>
          בית הוא לא מבנה.
          <em> בית הוא הקשבה.</em>
        </h1>
        <button className={s.apertureCta} onClick={onOpenLead}>
          <span>פגישת היכרות</span>
          <span className={s.apertureCtaArrow}>←</span>
        </button>
      </div>
    </div>
  );
}


/* ════════════════════════════════════════════════════
   VARIANT VI - TRIPTYCH
   Three vertical image panels with thin gold dividers.
   Floating editorial card crosses the center.
   Inspired by Herzog & de Meuron / Norm Architects.
   ════════════════════════════════════════════════════ */
function VariantTriptych({ onOpenLead }) {
  const PANELS = [
    { img: IMG.triptych[0], num: '01', label: 'Residence Z', loc: 'Tel Aviv' },
    { img: IMG.triptych[1], num: '02', label: 'Residence G', loc: 'Yokneam' },
    { img: IMG.triptych[2], num: '03', label: 'Residence S', loc: 'Alonei Aba' },
  ];

  return (
    <div className={s.triptych}>
      <div className={s.triptychPanels}>
        {PANELS.map((p, i) => (
          <div key={i} className={s.triptychPanel}>
            <img src={p.img} alt="" className={s.triptychImg} />
            <div className={s.triptychImgOverlay} />
            <span className={s.triptychPanelNum}>{p.num}</span>
            <div className={s.triptychPanelMeta}>
              <span>{p.label}</span>
              <span className={s.triptychPanelDot}>·</span>
              <span>{p.loc}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Vertical dividers */}
      <span className={`${s.triptychDivider} ${s.triptychDividerA}`} />
      <span className={`${s.triptychDivider} ${s.triptychDividerB}`} />

      {/* Floating editorial card */}
      <div className={s.triptychOverlay}>
        <div className={s.triptychTop}>
          <span className={s.triptychTopLine} />
          <span className={s.triptychTopText}>STUDIO LANGER · MMVIII</span>
          <span className={s.triptychTopLine} />
        </div>

        <h1 className={s.triptychHeadline}>
          <span className={s.triptychHl1}>Total</span>
          <span className={s.triptychHl2}>Design</span>
        </h1>
        <p className={s.triptychSub}>
          תכנון אדריכלי ועיצוב פנים כמכלול שלם -
          מהתוכנית הראשונה ועד לפרט האחרון.
        </p>

        <button className={s.triptychCta} onClick={onOpenLead}>
          <span>לפגישת היכרות</span>
          <span className={s.triptychCtaArrow}>←</span>
        </button>
      </div>

      {/* Bottom strip */}
      <div className={s.triptychBottom}>
        <span className={s.triptychBotPart}>Architecture</span>
        <span className={s.triptychBotDot}>✦</span>
        <span className={s.triptychBotPart}>Interiors</span>
        <span className={s.triptychBotDot}>✦</span>
        <span className={s.triptychBotPart}>Total Design</span>
      </div>
    </div>
  );
}


/* ════════════════════════════════════════════════════
   VARIANT VII - DISPATCH
   Sparse data-driven hero. Coordinates, year axis,
   left-aligned editorial inside dark cinematic frame.
   Inspired by Foster + Partners / OMA dossier pages.
   ════════════════════════════════════════════════════ */
function VariantDispatch({ onOpenLead }) {
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [showRest, setShowRest] = useState(false);
  const transitioning = useRef(false);
  const imagesRef = useRef([]);
  const zoomRef = useRef(null);
  const timerRef = useRef(null);
  const contentRef = useRef(null);

  const images = isMobile ? IMG.dispatchMobile : IMG.dispatchDesktop;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Defer loading the non-LCP hero images so the first image (LCP) gets full bandwidth
  useEffect(() => {
    if (showRest) return;
    const t = setTimeout(() => setShowRest(true), 2600);
    const onLoad = () => setShowRest(true);
    window.addEventListener('load', onLoad, { once: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener('load', onLoad);
    };
  }, [showRest]);

  const startZoom = useCallback((idx) => {
    zoomRef.current?.kill();
    const el = imagesRef.current[idx];
    if (!el) return;
    gsap.set(el, { scale: 1.12 });
    zoomRef.current = gsap.to(el, {
      scale: 1.02,
      duration: (DISPATCH_INTERVAL + DISPATCH_TRANSITION * 1000) / 1000,
      ease: 'sine.out',
    });
  }, []);

  const transitionTo = useCallback(
    (nextIdx) => {
      if (transitioning.current) return;
      transitioning.current = true;
      const curEl = imagesRef.current[current];
      const nxtEl = imagesRef.current[nextIdx];
      if (!curEl || !nxtEl) {
        transitioning.current = false;
        return;
      }
      const tl = gsap.timeline({
        onComplete: () => {
          setCurrent(nextIdx);
          transitioning.current = false;
        },
      });
      gsap.set(nxtEl, {
        clipPath: isMobile ? 'inset(100% 0 0 0)' : 'inset(0 0 0 100%)',
        opacity: 1,
        scale: 1.12,
        zIndex: 2,
      });
      startZoom(nextIdx);
      tl.to(
        nxtEl,
        {
          clipPath: 'inset(0 0 0 0)',
          duration: DISPATCH_TRANSITION,
          ease: 'power2.inOut',
        },
        0
      );
      tl.to(
        curEl,
        {
          opacity: 0,
          duration: 0.7,
          ease: 'sine.out',
        },
        DISPATCH_TRANSITION - 0.4
      );
      tl.set(curEl, { zIndex: 1 });
      tl.set(nxtEl, { zIndex: 1 });
    },
    [current, isMobile, startZoom]
  );

  // Initial first-slide setup
  useEffect(() => {
    const firstEl = imagesRef.current[0];
    if (firstEl) {
      gsap.set(firstEl, { opacity: 1, scale: 1.12, zIndex: 1 });
      startZoom(0);
    }
  }, [startZoom, isMobile]);

  // Auto-advance timer
  useEffect(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(
      () => transitionTo((current + 1) % images.length),
      DISPATCH_INTERVAL
    );
    return () => clearInterval(timerRef.current);
  }, [current, images.length, transitionTo]);

  // Staggered intro for content
  useEffect(() => {
    if (!contentRef.current) return;
    const els = contentRef.current.querySelectorAll('[data-r]');
    gsap.set(els, { y: 24, opacity: 0 });
    const tl = gsap.timeline({ delay: 0.35 });
    tl.to(els, {
      y: 0,
      opacity: 1,
      duration: 1.1,
      ease: 'power3.out',
      stagger: 0.12,
    });
    return () => tl.kill();
  }, []);

  const goTo = (idx) => {
    if (idx === current || transitioning.current) return;
    clearInterval(timerRef.current);
    transitionTo(idx);
    timerRef.current = setInterval(
      () => transitionTo((idx + 1) % images.length),
      DISPATCH_INTERVAL
    );
  };

  return (
    <div className={s.dispatch}>
      {/* Slideshow stack */}
      <div className={s.dispatchSlides}>
        {images.map((src, i) => (
          <img
            key={src}
            ref={(el) => (imagesRef.current[i] = el)}
            src={i === 0 || showRest ? src : undefined}
            alt=""
            className={s.dispatchImg}
            style={{ opacity: i === 0 ? 1 : 0 }}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding={i === 0 ? 'sync' : 'async'}
            fetchPriority={i === 0 ? 'high' : 'low'}
          />
        ))}
      </div>

      <div className={s.dispatchOverlay} />
      <div className={s.dispatchVignette} />

      {/* Top - masthead */}
      <div className={s.dispatchMasthead}>
        <div className={s.dispatchMastheadRow}>
          <span className={s.dispatchMastheadLine} />
          <h2 className={s.dispatchMastheadText}>LANGER</h2>
          <span className={s.dispatchMastheadLine} />
        </div>
        <span className={s.dispatchMastheadSub}>Architecture &amp; Design Studio</span>
      </div>

      {/* Centered editorial content */}
      <div className={s.dispatchContent} ref={contentRef}>
        <h1 className={s.dispatchHeadline} data-r>
          <span className={s.dispatchHeadlineEm}>יוקרה שקטה</span>
          {' '}בכל פרט
        </h1>
        <p className={s.dispatchSub} data-r>
          סטודיו בוטיק לאדריכלות ועיצוב פנים בגישת ה<span className={s.dispatchSubEm}>Total Design</span>
          <br />
          ליווי אישי מהקו הראשון על הנייר ועד פרט הגימור האחרון.
          <br />
          לבתים שנבנים סביב אורח החיים שלכם
        </p>
        <div className={s.dispatchCtas} data-r>
          <button className={s.dispatchCta} onClick={onOpenLead}>
            <span className={s.dispatchCtaText}>לשיחת היכרות לחצו כאן</span>
          </button>
          <button
            type="button"
            className={`${s.dispatchCta} ${s.dispatchCtaGhost}`}
            onClick={() => {
              const el = document.getElementById('projects');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            <span className={s.dispatchCtaText}>לצפייה בפרויקטים</span>
          </button>
        </div>
      </div>

      {/* Bottom-left scroll indicator */}
      <div className={s.dispatchScroll}>
        <span className={s.dispatchScrollLine}>
          <span className={s.dispatchScrollBead} />
        </span>
        <span className={s.dispatchScrollText}>SCROLL</span>
      </div>

      {/* Bottom-center counter + pips */}
      <div className={s.dispatchPipsWrap}>
        <span className={s.dispatchCounter}>
          <span className={s.dispatchCounterNum}>
            {String(current + 1).padStart(2, '0')}
          </span>
          <span className={s.dispatchCounterBar} />
          <span className={s.dispatchCounterTotal}>
            {String(images.length).padStart(2, '0')}
          </span>
        </span>
        <div className={s.dispatchPips}>
          {images.map((_, i) => (
            <button
              key={i}
              className={`${s.dispatchPip} ${i === current ? s.dispatchPipOn : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
            >
              <span className={s.dispatchPipFill} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ════════════════════════════════════════════════════
   VARIANT VIII - MARQUEE
   Image dominant on top, kinetic typographic band below.
   Inspired by BIG / Bureau Borsche / kinetic editorial.
   ════════════════════════════════════════════════════ */
function VariantMarquee({ onOpenLead }) {
  const WORDS = ['אדריכלות', 'TOTAL DESIGN', 'עיצוב פנים', 'ALONEI ABA', 'INTERIORS', 'EST · MMVIII'];
  const repeated = [...WORDS, ...WORDS, ...WORDS];

  return (
    <div className={s.marquee}>
      {/* Image area */}
      <div className={s.marqueeImageArea}>
        <img src={IMG.marquee} alt="" className={s.marqueeImg} />
        <div className={s.marqueeImgOverlay} />

        {/* Top bar */}
        <div className={s.marqueeTop}>
          <div className={s.marqueeTopL}>
            <span className={s.marqueeTopMark}>L/A</span>
            <span className={s.marqueeTopLine} />
            <span className={s.marqueeTopText}>Studio Langer · Architecture &amp; Interior Design</span>
          </div>
          <span className={s.marqueeTopYear}>EST · 2008</span>
        </div>

        {/* Center editorial */}
        <div className={s.marqueeCenter}>
          <span className={s.marqueeTag}>פרק ראשון · המפגש</span>
          <h1 className={s.marqueeHeadline}>
            <span className={s.marqueeHlMain}>בית, מהקו הראשון</span>
            <span className={s.marqueeHlAccent}>עד הפרט האחרון</span>
          </h1>
          <button className={s.marqueeCta} onClick={onOpenLead}>
            <span>תיאום פגישת היכרות</span>
            <span className={s.marqueeCtaArrow}>←</span>
          </button>
        </div>

        {/* Bottom-left small caption */}
        <div className={s.marqueeCaption}>
          <span className={s.marqueeCapNum}>I / VIII</span>
          <span className={s.marqueeCapDot}>·</span>
          <span>Residence - Alonei Aba</span>
        </div>
      </div>

      {/* Bottom marquee band */}
      <div className={s.marqueeBand}>
        <div className={s.marqueeTrack}>
          {repeated.map((t, i) => (
            <span key={i} className={s.marqueeWord}>
              {t}
              <span className={s.marqueeStar}>✦</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ════════════════════════════════════════════════════
   WRAPPER - with in-hero variant switcher
   ════════════════════════════════════════════════════ */
const VARIANTS = [
  { id: 0, roman: 'I',    name: 'ATELIER'  },
  { id: 1, roman: 'II',   name: 'MANOR'    },
  { id: 2, roman: 'III',  name: 'SALON'    },
  { id: 3, roman: 'IV',   name: 'INDEX'    },
  { id: 4, roman: 'V',    name: 'APERTURE' },
  { id: 5, roman: 'VI',   name: 'TRIPTYCH' },
  { id: 6, roman: 'VII',  name: 'DISPATCH' },
  { id: 7, roman: 'VIII', name: 'MARQUEE'  },
];

const RENDERERS = [
  VariantAtelier,
  VariantManor,
  VariantSalon,
  VariantIndex,
  VariantAperture,
  VariantTriptych,
  VariantDispatch,
  VariantMarquee,
];

export default function HeroVariants({ onOpenLead }) {
  return (
    <div className={s.wrapper}>
      <div className={s.stage}>
        <VariantDispatch onOpenLead={onOpenLead} />
      </div>
    </div>
  );
}
